import {
    Box,
    Button,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import {
    CheckCircleOutlined,
    CloudDownload,
    Help,
    HourglassTop,
    Lock,
    People,
    PersonAdd,
    RemoveCircleOutlineOutlined,
} from '@mui/icons-material';
import {
    Link,
    Outlet,
    useLocation,
    useNavigate,
    useOutletContext,
    useParams,
} from 'react-router-dom';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import {
    OperatorParkingaboUserInviteForm,
    OperatorParkingaboUserCreateForm,
    ParkingaboUser,
} from './OperatorParkingaboUsersForm.tsx';
import {
    RequestMethod,
    RequestStatus,
    ServerRequestState,
    useServerFetch,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { LoadingSpinnerPresets } from 'dg-web-shared/common/components/material-ui/PresetLoadingSpinner.tsx';
import {
    FilterConfig,
    makeSearchQueryWithFilters,
} from '../shared-mui-components/filter/OperatorFilterHelpers.tsx';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { getCustomerDisplayName } from 'dg-web-shared/common/models/Users.tsx';
import { MainContent } from '../shared-mui-components/MainContent.tsx';
import {
    EmptyResults,
    FetchError,
    ResultLimitAlert,
} from '../shared-mui-components/ListMessages.tsx';
import { HeaderWithButtons } from '../shared-mui-components/HeaderWithButtons.tsx';
import { FiltersBox } from '../shared-mui-components/filter/FiltersBox.tsx';
import { useParkingaboUserFilterConfiguration } from '../shared-mui-components/filter/OperatorFilterConfiguration.tsx';
import { Language } from 'dg-web-shared/lib/Localized.ts';
import {
    ButtonWithDropdown,
    DropdownItem,
} from '../layout/components/ButtonWithDropdown.tsx';
import { useMemo, useState } from 'react';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../ui/modal/OperatorRoutedModal.tsx';
import { CustomerTenantState } from 'product-shared/parkingabo/CustomerTenantState.ts';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';
import { openNewTab } from 'dg-web-shared/common/utils/Navigation.tsx';
import { urlWithOperatorExternalId } from '../utils/Utils.ts';
import { ParkingaboRole } from '../parkingabo-role/OperatorParkingaboRole.ts';
import { ParkingaboCustomerPaymentMode } from 'product-shared/parkingabo/ParkingaboOperatingMode.tsx';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from '../operator-logins/components/ConfirmationDialog.tsx';

export interface CustomerSearchResult {
    eof: boolean;
    searchTerms: string[];
    results: ParkingaboCustomer[];
}

interface ParkingaboCustomer {
    customerId: number;
    roleId: string | null;
    customerNr: string;
    firstName: string | null;
    lastName: string | null;
    language: Language;
    reference: string | null;
    onboardingCompleteTime: string | null;
    state: CustomerTenantState;
    email: string | null;
}

export interface LifecycleErrorResponse {
    error: LifecycleError;
}

export enum LifecycleError {
    TENANT_IS_DEACTIVATED = 'TENANT_IS_DEACTIVATED',
    ROLE_IS_DEACTIVATED = 'ROLE_IS_DEACTIVATED',
}

function HeaderButtons({
    roles,
    listState,
    searchText,
    activeFilters,
}: {
    roles: ParkingaboRole[];
    listState: ServerRequestState<CustomerSearchResult, CustomerSearchResult>;
    searchText: string | null;
    activeFilters: FilterConfig;
}) {
    return (
        <Stack
            direction="row"
            spacing={3}
            sx={{
                alignItems: 'center',
            }}
        >
            <UserLifecycleInfo
                hasRoleWithAppPayment={roles.some(
                    role =>
                        role.paymentMode ==
                        ParkingaboCustomerPaymentMode.APP_PAYMENT,
                )}
            />
            <ExportButton listState={listState} />
            <CreateParkingaboUserButton
                roles={roles}
                searchText={searchText}
                activeFilters={activeFilters}
            />
        </Stack>
    );
}

function UserLifecycleInfo({
    hasRoleWithAppPayment,
}: {
    hasRoleWithAppPayment: boolean;
}) {
    const [infoButtonOpen, setInfoButtonOpen] = useState(false);
    return (
        <>
            <Button
                variant="outlined"
                startIcon={<Help />}
                onClick={() => setInfoButtonOpen(true)}
            >
                <Localized
                    de="Autom. Aktionen"
                    fr="Actions autom."
                    it="Azioni autom."
                    en="Automatic actions"
                />
            </Button>
            <ConfirmationDialog
                open={infoButtonOpen}
                title={
                    <Localized
                        de="Autom. Aktionen"
                        fr="Actions autom."
                        it="Azioni autom."
                        en="Automatic actions"
                    />
                }
                confirmLabel={<Localized de="OK" fr="OK" it="OK" en="OK" />}
                onConfirm={() => setInfoButtonOpen(false)}
                dialogVersion={ConfirmationDialogVersions.PRIMARY}
            >
                <>
                    {hasRoleWithAppPayment && (
                        <AccountsWithOverduePaymentsInfo />
                    )}
                    <UnusedAccountsInfo
                        hasRoleWithAppPayment={hasRoleWithAppPayment}
                    />
                    {hasRoleWithAppPayment && <AccountsWithAppInfo />}
                    <AccountsWithoutAppInfo
                        hasRoleWithAppPayment={hasRoleWithAppPayment}
                    />
                    <ArchivedAccountsInfo />
                </>
            </ConfirmationDialog>
        </>
    );
}

function AccountsWithOverduePaymentsInfo() {
    return (
        <>
            <p>
                <b>
                    <Localized
                        de="KONTEN MIT ZAHLUNGSVERZUG"
                        fr="COMPTES AVEC ARRIÉRÉS DE PAIEMENT"
                        it="CONTI CON PAGAMENTI ARRETRATI"
                        en="ACCOUNTS WITH OVERDUE PAYMENTS"
                    />
                    <br />
                    <Localized
                        de="(ab Ende Oktober 2026)"
                        fr="(à partir de fin octobre 2026)"
                        it="(a partire dalla fine di ottobre 2026)"
                        en="(from the end of October 2026)"
                    />
                </b>
            </p>
            <p>
                <Localized
                    de="Falls ein Benutzerkonto mit App-Zahlung länger als 3 aufeinanderfolgende Kalendermonate einen negativen Saldo aufweist, werden automatisch folgende Schritte durchgeführt:"
                    fr="Si le compte utilisateur avec paiement via l'application est à découvert depuis plus de 3 mois civils consécutifs, les opérations suivantes sont automatiquement effectuées:"
                    it="Nel caso in cui il conto utente con pagamento via app si trova da più di 3 mesi di calendario consecutivi in negativo, vengono eseguite automaticamente le seguenti operazioni:"
                    en="If a user account with app payment has been in the red for more than three consecutive calendar months, the following actions are taken automatically:"
                />
                <ul>
                    <li>
                        <Localized
                            de="der ausstehende Betrag wird ausgebucht"
                            fr="le montant dû est comptabilisé en perte"
                            it="l'importo scoperto viene contabilizzato a perdita"
                            en="the outstanding amount is written off"
                        />
                    </li>
                    <li>
                        <Localized
                            de="das Benutzerkonto wird gesperrt"
                            fr="le compte utilisateur sera bloqué"
                            it="il conto utente verrà bloccato"
                            en="the user account will be locked"
                        />
                    </li>
                    <li>
                        <Localized
                            de="der Benutzer wird per E-Mail informiert"
                            fr="l'utilisateur sera informé par e-mail"
                            it="l'utente verrà informato via e-mail"
                            en="the user will be notified by email"
                        />
                    </li>
                </ul>
            </p>
        </>
    );
}

function UnusedAccountsInfo({
    hasRoleWithAppPayment,
}: {
    hasRoleWithAppPayment: boolean;
}) {
    return (
        <>
            <p>
                <b>
                    <Localized
                        de="UNBENUTZTE KONTEN"
                        fr="COMPTES INUTILISÉS"
                        it="CONTI INUTILIZZATI"
                        en="UNUSED ACCOUNTS"
                    />
                    {!hasRoleWithAppPayment && (
                        <>
                            <br />
                            <Localized
                                de="(ab Ende März 2027)"
                                fr="(à partir de fin mars 2027)"
                                it="(a partire dalla fine di marzo 2027)"
                                en="(from the end of March 2027)"
                            />
                        </>
                    )}
                </b>
            </p>
            <p>
                <Localized
                    de="Die Konten werden als benutzt betrachtet, wenn sie mindestens ein gültiges Produkt (Ausnahme: Offstreet-Produkte ohne definiertes Enddatum) oder einen Parkvorgang haben."
                    fr="Les comptes sont considérés comme utilisés s'ils ont au moins un produit valide (exception: les produits offstreet sans date de fin définie) ou une procédure."
                    it="I conti vengono considerati utilizzati se hanno almeno un prodotto valido (eccezione: i prodotti offstreet senza una data di fine definita) o una procedura."
                    en="The accounts are considered used if they have at least one valid product (exception: offstreet products without a defined end date) or a procedure."
                />
            </p>
            <p>
                <Localized
                    de="Für die Berechnung der Dauer der Nichtnutzung ist das Ablaufdatum des letzten Produkts oder des letzten Parkvorgangs massgeblich."
                    fr="Pour le calcul de la durée d'inutilisation, la date de fin de validité du dernier produit ou de la dernière procédure de stationnement est prise en compte."
                    it="Per il calcolo della durata di inutilizzo, fa stato la data finale di validita dell'ultimo prodotto o dell'ultima procedura di parcheggio."
                    en="For the calculation of the duration of non-use, the end date of validity of the last product or the last parking procedure applies."
                />
            </p>
        </>
    );
}

function AccountsWithAppInfo() {
    return (
        <>
            <p>
                <b>
                    <Localized
                        de="Konten mit App"
                        fr="Comptes avec app"
                        it="Conti con app"
                        en="Accounts with App"
                    />
                    <br />
                    <Localized
                        de="(ab Ende Oktober 2026)"
                        fr="(à partir de fin octobre 2026)"
                        it="(a partire dalla fine di ottobre 2026)"
                        en="(from the end of October 2026)"
                    />
                </b>
            </p>
            <p>
                <Localized
                    de="Falls ein Benutzerkonto mit App länger als 6 aufeinanderfolgende Kalendermonate unbenutzt bleibt, werden automatisch folgende Schritte durchgeführt:"
                    fr="Si le compte utilisateur avec app reste inutilisé depuis plus de 6 mois civils consécutifs, les opérations suivantes sont automatiquement effectuées:"
                    it="Nel caso in cui un conto utente con app resti inutilizzato per più di 6 mesi di calendario consecutivi, vengono eseguite automaticamente le seguenti operazioni:"
                    en="If a user account with app remains unused for more than 6 consecutive calendar months, the following actions are taken automatically:"
                />
                <ul>
                    <li>
                        <Localized
                            de="der Zugang via App und das Zahlungsmittel werden gelöscht"
                            fr="l'accès via l'app et le moyen de paiement seront supprimé"
                            it="l'accesso via app e il mezzo di pagamento verranno cancellati"
                            en="the access via app and the payment means will be deleted"
                        />
                    </li>
                    <li>
                        <Localized
                            de="der Benutzer wird per E-Mail informiert"
                            fr="l'utilisateur sera informé par e-mail"
                            it="l'utente verrà informato via e-mail"
                            en="the user will be notified by email"
                        />
                    </li>
                    <li>
                        <Localized
                            de="Ab diesem Zeitpunkt wird die monatliche Lizenzgebühr «App-Benutzer» für dieses Konto nicht mehr berechnet."
                            fr="À partir de maintenant, la licence mensuelle «Utilisateur avec app» ne sera plus facturée pour ce compte."
                            it="Da questo momento in poi, per questo conto, non verrà più addebitata la licenza mensile «Utente con app»."
                            en="From now on, the monthly “App User” license fee will no longer be charged to this account."
                        />
                    </li>
                </ul>
            </p>
        </>
    );
}

function AccountsWithoutAppInfo({
    hasRoleWithAppPayment,
}: {
    hasRoleWithAppPayment: boolean;
}) {
    return (
        <>
            {hasRoleWithAppPayment && (
                <p>
                    <b>
                        <Localized
                            de="Konten ohne App"
                            fr="Comptes sans app"
                            it="Conti senza app"
                            en="Accounts without App"
                        />
                        <br />
                        <Localized
                            de="(ab Ende März 2027)"
                            fr="(à partir de fin mars 2027)"
                            it="(a partire dalla fine di marzo 2027)"
                            en="(from the end of March 2027)"
                        />
                    </b>
                </p>
            )}
            <p>
                <Localized
                    de="Falls ein Benutzerkonto ohne App länger als 12 aufeinanderfolgende Kalendermonate unbenutzt bleibt, werden automatisch folgende Schritte durchgeführt:"
                    fr="Si le compte utilisateur sans app reste inutilisé depuis plus de 12 mois civils consécutifs, les opérations suivantes sont automatiquement effectuées:"
                    it="Nel caso in cui un conto utente senza app resti inutilizzato per più di 12 mesi di calendario consecutivi, vengono eseguite automaticamente le seguenti operazioni:"
                    en="If a user account without app remains unused for more than 12 consecutive calendar months, the following actions are taken automatically:"
                />
                <ul>
                    <li>
                        <Localized
                            de="das Benutzerkonto wird archiviert"
                            fr="le compte utilisateur sera archivé"
                            it="il conto utente verrà archiviato"
                            en="the user account will be archived"
                        />
                    </li>
                </ul>
            </p>
        </>
    );
}

function ArchivedAccountsInfo() {
    return (
        <>
            <p>
                <b>
                    <Localized
                        de="ARCHIVIERTE KONTEN"
                        fr="COMPTES ARCHIVÉS"
                        it="CONTI ARCHIVIATI"
                        en="ARCHIVED ACCOUNTS"
                    />
                    <br />
                    <Localized
                        de="(ab Ende März 2027)"
                        fr="(à partir de fin mars 2027)"
                        it="(a partire dalla fine di marzo 2027)"
                        en="(from the end of March 2027)"
                    />
                </b>
            </p>
            <p>
                <Localized
                    de="Falls ein Benutzerkonto länger als 18 aufeinanderfolgende Kalendermonate archiviert bleibt, wird automatisch folgende Schritt durchgeführt:"
                    fr="Si le compte utilisateur reste archivé pendant plus de 18 mois civils consécutifs, le processus suivant sera automatiquement effectué:"
                    it="Nel caso in cui il conto utente resti archiviato per più di 18 mesi di calendario consecutivi, verrà eseguita automaticamente la seguente operazione:"
                    en="If a user account remains archived for more than 18 consecutive calendar months, the following action is taken automatically:"
                />
                <ul>
                    <li>
                        <Localized
                            de="das Benutzerkonto wird gelöscht"
                            fr="le compte utilisateur sera supprimé"
                            it="il conto utente verrà eliminato"
                            en="the user account will be deleted"
                        />
                    </li>
                </ul>
            </p>
        </>
    );
}

function ExportButton({
    listState,
}: {
    listState: ServerRequestState<CustomerSearchResult, CustomerSearchResult>;
}) {
    const { language } = useLanguage();
    const { search } = useLocation();
    const paramsWithLanguage = useMemo(() => {
        const params = new URLSearchParams(search);
        params.append('language', language);
        return params;
    }, [search, language]);
    const listReady =
        listState.status === RequestStatus.SUCCESS &&
        listState.data.results.length > 0;
    const {
        operatorData: { hasParkingaboUsageReportData },
        operatorExternalId,
    } = useOperatorContext();

    const divider: DropdownItem = { key: 'divider', divider: true };

    return (
        <ButtonWithDropdown
            variant="outlined"
            label={
                <Localized
                    de="Exportieren"
                    fr="Exporter"
                    it="Esporta"
                    en="Export"
                />
            }
            startIcon={<CloudDownload />}
            dropdownItems={[
                {
                    key: 'download-list',
                    label: (
                        <Localized de="Liste" fr="Liste" it="Lista" en="List" />
                    ),
                    onClickCallback: () => {
                        openNewTab(
                            urlWithOperatorExternalId(
                                `/ui-api/operator-account/parkingabo/customer/export?${paramsWithLanguage.toString()}`,
                                operatorExternalId,
                            ),
                        );
                    },
                    disabled: !listReady,
                    tooltip: !listReady ? (
                        <Localized
                            de="Liste nicht bereit"
                            fr="Liste non prête"
                            it="Lista non pronta"
                            en="List not ready"
                        />
                    ) : (
                        <Localized
                            de="Die Benutzerkonten, die den Such-/Filterkriterien entsprechen, werde im Excel-Format exportiert."
                            fr="Les comptes utilisateurs qui correspondent aux critères de recherche/filtrage sont exportées au format Excel."
                            it="I conti utente che corrispondono ai criteri di ricerca/filtro vengono esportati in formato Excel."
                            en="The user accounts that match the search/filter criteria are exported in Excel format."
                        />
                    ),
                },
                {
                    key: 'download-vehicle-list',
                    label: (
                        <Localized
                            de="Fahrzeuge"
                            fr="Véhicules"
                            it="Veicoli"
                            en="Vehicles"
                        />
                    ),
                    tooltip: !listReady ? (
                        <Localized
                            de="Liste nicht bereit"
                            fr="Liste non prête"
                            it="Lista non pronta"
                            en="List not ready"
                        />
                    ) : (
                        <Localized
                            de="Die Fahrzeuge in den Benutzerkonten, die den Such-/Filterkriterien entsprechen, werde im Excel-Format exportiert."
                            fr="Les véhicules enregistrés dans les comptes utilisateurs qui correspondent aux critères de recherche/filtrage sont exportées au format Excel."
                            it="I veicoli contenuti nei conti utente che corrispondono ai criteri di ricerca/filtro vengono esportati in formato Excel."
                            en="The vehicles contained in the user accounts that match the search/filter criteria are exported in Excel format."
                        />
                    ),
                    onClickCallback: () => {
                        openNewTab(
                            urlWithOperatorExternalId(
                                `/ui-api/operator-account/parkingabo/customer-vehicles/export?${paramsWithLanguage.toString()}`,
                                operatorExternalId,
                            ),
                        );
                    },
                    disabled: !listReady,
                },
                divider,
                {
                    key: 'download-usage-report',
                    label: (
                        <Localized
                            de="Nutzungsbericht - Übersicht"
                            fr="Rapport d'usage - aperçu"
                            it="Resoconto d'utilizzo - panoramica"
                            en="Usage report - overview"
                        />
                    ),
                    tooltip: !hasParkingaboUsageReportData ? (
                        <Localized
                            de="Noch keine Daten verfügbar; die Daten werden am Ende des Monats erstellt und sind für die letzten 18 Monate sichtbar."
                            fr="Aucune donnée n'est encore disponible; les données sont générées à la fin du mois et sont visibles pour les 18 derniers mois."
                            it="Ancora nessun dato disponibile; i dati vengono generati a fine mese e sono visibili per gli ultimi 18 mesi."
                            en="No data available yet; data are generated at the end of the month and are visible for the last 18 months."
                        />
                    ) : (
                        <Localized
                            de="Der Nutzungsbericht für die letzten 18 vollen Monate wird im Excel-Format exportiert."
                            fr="Le rapport d'usage des 18 derniers mois complets est exporté sous forme de fichier Excel."
                            it="Il resoconto di utilizzo degli ultimi 18 mesi interi viene esportato in formato Excel."
                            en="The usage report for the last 18 full months is exported as an Excel file."
                        />
                    ),
                    onClickCallback: () => {
                        window.open(
                            urlWithOperatorExternalId(
                                `/ui-api/operator-account/parkingabo/customer-usage-report/export?${paramsWithLanguage.toString()}`,
                                operatorExternalId,
                            ),
                            '_blank',
                            'noopener,noreferrer',
                        );
                    },
                    disabled: !hasParkingaboUsageReportData,
                },
                {
                    key: 'download-usage-report-detail',
                    label: (
                        <Localized
                            de="Nutzungsbericht - Einzelne Parkvorgänge"
                            fr="Rapport d'usage - procédures individuelles"
                            it="Resoconto d'utilizzo - singole soste"
                            en="Usage report - individual transactions"
                        />
                    ),
                    tooltip: (
                        <Localized
                            de="Der detaillierte Nutzungsbericht für die letzten 18 vollen Monate (ab Juli 2026), der die einzelnen Parkvorgänge enthält, wird im Excel-Format exportiert."
                            fr="Le rapport d'usage détaillé des 18 derniers mois complets (à partir de juillet 2026), qui contient les procédures individuelles, est exporté sous forme de fichier Excel."
                            it="Il resoconto di utilizzo dettagliato degli ultimi 18 mesi interi (a partire da luglio 2026), che contiene le singole soste, viene esportato in formato Excel."
                            en="The detailed usage report for the last 18 full months (from July 2026), that includes the individual transactions, is exported as an Excel file."
                        />
                    ),
                    onClickCallback: () => {
                        window.open(
                            urlWithOperatorExternalId(
                                `/ui-api/operator-account/parkingabo/customer-usage-report-detail/export?${paramsWithLanguage.toString()}`,
                                operatorExternalId,
                            ),
                            '_blank',
                            'noopener,noreferrer',
                        );
                    },
                    disabled: false,
                },
            ]}
        />
    );
}

function getRolesDropdownOptions(
    roles: ParkingaboRole[],
    onClick: (roleId: string) => void,
): DropdownItem[] {
    return roles.map(role => ({
        key: role.roleId,
        label: <Localized {...role.name} />,
        disabled: false,
        onClickCallback: () => onClick(role.roleId),
    }));
}

function getInviteParkingaboUserUrl(
    selectedRoleId: string,
    searchText: string | null,
    activeFilters: FilterConfig,
) {
    return `invite/role/${selectedRoleId}${makeSearchQueryWithFilters(
        searchText,
        activeFilters,
    )}`;
}

function getCreateParkingaboUserUrl(
    selectedRoleId: string,
    searchText: string | null,
    activeFilters: FilterConfig,
) {
    return `add/role/${selectedRoleId}${makeSearchQueryWithFilters(
        searchText,
        activeFilters,
    )}`;
}

function CreateParkingaboUserButton({
    roles,
    searchText,
    activeFilters,
}: {
    roles: ParkingaboRole[];
    searchText: string | null;
    activeFilters: FilterConfig;
}) {
    const navigate = useNavigate();
    const { language } = useLanguage();
    const activeRoles = roles.filter(role => !role.deactivated);
    const rolesWithCreateUserFunction = activeRoles
        .filter(
            role =>
                role.paymentMode !== ParkingaboCustomerPaymentMode.APP_PAYMENT,
        )
        .sort((a, b) => a.name[language].localeCompare(b.name[language]));
    const rolesWithInviteUserFunction = activeRoles
        .filter(
            role =>
                role.paymentMode ===
                    ParkingaboCustomerPaymentMode.APP_PAYMENT &&
                !role.hasAppSignupEmailDomains,
        )
        .sort((a, b) => a.name[language].localeCompare(b.name[language]));

    if (
        rolesWithCreateUserFunction.length === 0 &&
        rolesWithInviteUserFunction.length === 0
    ) {
        return null;
    }

    const roleWithInviteOptions = getRolesDropdownOptions(
        rolesWithInviteUserFunction,
        roleId => {
            navigate(
                getInviteParkingaboUserUrl(roleId, searchText, activeFilters),
            );
        },
    );
    const roleWithCreateOptions = getRolesDropdownOptions(
        rolesWithCreateUserFunction,
        roleId => {
            navigate(
                getCreateParkingaboUserUrl(roleId, searchText, activeFilters),
            );
        },
    );

    const divider: DropdownItem = { key: 'divider', divider: true };

    const hasOnlyOneRoleOption =
        rolesWithInviteUserFunction.length +
            rolesWithCreateUserFunction.length ===
        1;
    if (hasOnlyOneRoleOption) {
        return (
            <Link
                to={
                    rolesWithCreateUserFunction.length > 0
                        ? getCreateParkingaboUserUrl(
                              rolesWithCreateUserFunction[0].roleId,
                              searchText,
                              activeFilters,
                          )
                        : getInviteParkingaboUserUrl(
                              rolesWithInviteUserFunction[0].roleId,
                              searchText,
                              activeFilters,
                          )
                }
            >
                <Button
                    variant="contained"
                    startIcon={<PersonAdd />}
                    color="primary"
                >
                    <Localized
                        de="Neues Konto"
                        fr="Nouveaux compte"
                        it="Nuovo conto"
                        en="New account"
                    />
                </Button>
            </Link>
        );
    }

    const hasRolesForBothOptions =
        roleWithCreateOptions.length > 0 && roleWithInviteOptions.length > 0;
    return (
        <ButtonWithDropdown
            label={
                <Localized
                    de="Neues Konto"
                    fr="Nouveaux compte"
                    it="Nuovo conto"
                    en="New account"
                />
            }
            startIcon={<PersonAdd />}
            dropdownItems={[
                ...(hasRolesForBothOptions
                    ? [
                          {
                              key: 'create',
                              label: (
                                  <Localized
                                      de="ERFASSEN"
                                      fr="SAISIR"
                                      it="REGISTRA"
                                      en="CAPTURE"
                                  />
                              ),
                              disabled: true,
                              onClickCallback: () => null,
                          },
                      ]
                    : []),
                ...roleWithCreateOptions,

                ...(hasRolesForBothOptions
                    ? [
                          divider,
                          {
                              key: 'invite',
                              label: (
                                  <Localized
                                      de="EINLADEN"
                                      fr="INVITER"
                                      it="INVITA"
                                      en="INVITE"
                                  />
                              ),
                              disabled: true,
                              onClickCallback: () => null,
                          },
                      ]
                    : []),
                ...roleWithInviteOptions,
            ]}
        />
    );
}

export function OperatorParkingaboUsers() {
    const navigate = useNavigate();

    const {
        filterConfig,
        activeFilters,
        listState,
        refetchList,
        searchText,
        filterConfigState,
    } = useParkingaboUserFilterConfiguration();

    const [roleState] = useServerFetch<ParkingaboRole[], object>(
        () => ({
            url: '/ui-api/operator-account/parkingabo/roles',
        }),
        {},
    );
    const [customerLifecycleState, setCustomerLifecycle] = useServerWrite<
        { state: CustomerTenantState; customerNr: string },
        ParkingaboUser,
        LifecycleErrorResponse
    >(({ customerNr }) => ({
        url: `/ui-api/operator-account/parkingabo/customer/${customerNr}/lifecycle`,
        method: RequestMethod.PUT,
    }));

    useServerSuccessEffect(customerLifecycleState, refetchList);

    return (
        <OperatorAsyncLoadedSection
            requestState={roleState}
            render={roles => {
                return (
                    <MainContent>
                        <HeaderWithButtons
                            title={
                                <Localized
                                    de="Benutzerkonti"
                                    fr="Comptes utilisateur"
                                    it="Conti utente"
                                    en="User accounts"
                                />
                            }
                            icon={People}
                            headerButtons={
                                <HeaderButtons
                                    roles={roles}
                                    listState={listState}
                                    searchText={searchText}
                                    activeFilters={activeFilters}
                                />
                            }
                        />
                        <FiltersBox
                            searchText={searchText}
                            activeFilters={activeFilters}
                            filterConfig={filterConfig}
                            requestState={listState}
                            refetch={refetchList}
                            pending={
                                filterConfigState.status ===
                                RequestStatus.PENDING
                            }
                            tooltip={<ParkingaboUsersSearchTooltip />}
                        />
                        <OperatorParkingaboUserTable
                            listState={listState}
                            roles={roles}
                            refetchList={refetchList}
                            onRowClick={(customerNr: string) =>
                                navigate(
                                    `${customerNr}/detail${makeSearchQueryWithFilters(
                                        searchText,
                                        activeFilters,
                                    )}`,
                                )
                            }
                        />

                        <ParkingaboUserOutlet
                            context={{
                                roles: roles,
                                customerLifecycleState: customerLifecycleState,
                                setCustomerLifecycle: setCustomerLifecycle,
                            }}
                        />
                    </MainContent>
                );
            }}
        />
    );
}

export function useParkingaboUserOutletContext() {
    return useOutletContext<ParkingaboUserContext>();
}

function ParkingaboUserOutlet({ context }: { context: ParkingaboUserContext }) {
    return <Outlet context={context} />;
}

export interface ParkingaboUserContext {
    roles: ParkingaboRole[];
    customerLifecycleState: ServerRequestState<
        ParkingaboUser,
        LifecycleErrorResponse
    >;
    setCustomerLifecycle: (state: {
        state: CustomerTenantState;
        customerNr: string;
    }) => void;
}

export function OperatorParkingaboAddUser() {
    const { roles } = useParkingaboUserOutletContext();
    const { activeFilters, refetchList, searchText } =
        useParkingaboUserFilterConfiguration();
    const { roleId } = useParams();

    const roleOfUser = getRoleOfUser(roles, roleId ?? null);

    if (!roleOfUser) {
        return null;
    }

    return (
        <OperatorRoutedModal
            variant={ModalVariant.STANDARD}
            backUrl={`..${makeSearchQueryWithFilters(
                searchText,
                activeFilters,
            )}`}
            render={controller => (
                <OperatorParkingaboUserCreateForm
                    onCreated={newCustomerNr => {
                        controller.setShouldConfirmBeforeLeave(false);
                        refetchList();
                        controller.safeNavigateTo(`../${newCustomerNr}/detail`);
                    }}
                    roleOfUser={roleOfUser}
                    roles={roles}
                    onClose={controller.close}
                    onDirtyStateChange={controller.setShouldConfirmBeforeLeave}
                />
            )}
        />
    );
}

export function OperatorParkingaboInviteUser() {
    const { roles } = useParkingaboUserOutletContext();
    const { activeFilters, refetchList, searchText } =
        useParkingaboUserFilterConfiguration();
    const { roleId } = useParams();

    const roleOfUser = getRoleOfUser(roles, roleId ?? null);

    if (!roleOfUser) {
        return null;
    }

    return (
        <OperatorRoutedModal
            variant={ModalVariant.STANDARD}
            backUrl={`..${makeSearchQueryWithFilters(
                searchText,
                activeFilters,
            )}`}
            render={controller => (
                <OperatorParkingaboUserInviteForm
                    onCreated={newCustomerNr => {
                        controller.setShouldConfirmBeforeLeave(false);
                        refetchList();
                        controller.safeNavigateTo(`../${newCustomerNr}/detail`);
                    }}
                    roleOfUser={roleOfUser}
                    roles={roles}
                    onClose={controller.close}
                    onDirtyStateChange={controller.setShouldConfirmBeforeLeave}
                />
            )}
        />
    );
}

function getRoleOfUser(
    roles: ParkingaboRole[],
    roleIdOfUser: string | null,
): ParkingaboRole | null {
    return roles.find(role => role.roleId === roleIdOfUser) ?? null;
}

function OperatorParkingaboUserTable({
    listState,
    roles,
    refetchList,
    onRowClick,
}: {
    listState: ServerRequestState<CustomerSearchResult, CustomerSearchResult>;
    refetchList: () => void;
    roles: ParkingaboRole[];
    onRowClick: (customerNr: string) => void;
}) {
    const { operatorData } = useOperatorContext();
    const showRoleColumn = operatorData.numberOfActiveParkingaboRoles > 1;

    return (
        <OperatorAsyncLoadedSection
            requestState={listState}
            pendingLoaderPreset={LoadingSpinnerPresets.FillAllSpaceAndCenter}
            render={results => {
                const isEmpty = results.results.length === 0;

                if (isEmpty) {
                    return <EmptyResults />;
                }

                return (
                    <>
                        <TableContainer>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <Localized
                                                de="Status"
                                                fr="Status"
                                                it="Status"
                                                en="Status"
                                            />
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                paddingLeft: 3.25,
                                            }}
                                        >
                                            <Localized
                                                de="Name"
                                                fr="Nom"
                                                it="Nome"
                                                en="Name"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized
                                                de="Referenz"
                                                fr="Référence"
                                                it="Referenza"
                                                en="Reference"
                                            />
                                        </TableCell>
                                        {showRoleColumn && (
                                            <TableCell
                                                sx={{
                                                    paddingRight: 3.25,
                                                }}
                                            >
                                                <Localized
                                                    de="Rolle"
                                                    fr="Rôle"
                                                    it="Ruolo"
                                                    en="Role"
                                                />
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <Localized
                                                de="ID"
                                                fr="ID"
                                                it="ID"
                                                en="ID"
                                            />
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {results.results.map(customer => {
                                        const roleOfUser = getRoleOfUser(
                                            roles,
                                            customer.roleId,
                                        );

                                        if (roleOfUser == null) {
                                            return null;
                                        }

                                        return (
                                            <TableRow
                                                key={customer.customerNr}
                                                hover
                                                onClick={() =>
                                                    onRowClick(
                                                        customer.customerNr,
                                                    )
                                                }
                                            >
                                                <TableCell>
                                                    <IconForCustomerStatus
                                                        status={customer.state}
                                                        onboardingCompleteTime={
                                                            customer.onboardingCompleteTime
                                                        }
                                                        paymentMode={
                                                            roleOfUser?.paymentMode
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        paddingLeft: 3.25,
                                                    }}
                                                >
                                                    {getCustomerDisplayName(
                                                        customer,
                                                        true,
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {customer.reference || '-'}
                                                </TableCell>
                                                {showRoleColumn && (
                                                    <TableCell
                                                        sx={{
                                                            paddingRight: 3.25,
                                                        }}
                                                    >
                                                        {roleOfUser && (
                                                            <Localized
                                                                {...roleOfUser.name}
                                                            />
                                                        )}
                                                    </TableCell>
                                                )}
                                                <TableCell>
                                                    {customer.customerNr}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                            <ResultLimitAlert
                                eof={results.eof}
                                numberOfResults={results.results.length}
                                hasExport={true}
                            />
                        </TableContainer>
                    </>
                );
            }}
            renderError={() => <FetchError refetchList={refetchList} />}
        />
    );
}

export function IconForCustomerStatus({
    status,
    onboardingCompleteTime,
    paymentMode,
}: {
    status: CustomerTenantState;
    onboardingCompleteTime: string | null;
    paymentMode: ParkingaboCustomerPaymentMode;
}) {
    switch (status) {
        case CustomerTenantState.ACTIVE:
            if (
                onboardingCompleteTime ||
                paymentMode !== ParkingaboCustomerPaymentMode.APP_PAYMENT
            ) {
                return (
                    <Tooltip
                        title={
                            <Localized
                                de="aktiv"
                                fr="actif"
                                it="attivo"
                                en="active"
                            />
                        }
                        placement="right"
                    >
                        <CheckCircleOutlined color="success" />
                    </Tooltip>
                );
            }
            return (
                <Tooltip
                    title={
                        <Localized
                            de="pendent"
                            fr="en attente"
                            it="in attesa"
                            en="pending"
                        />
                    }
                    placement="right"
                >
                    <HourglassTop sx={{ color: 'warning.main' }} />
                </Tooltip>
            );
        case CustomerTenantState.LOCKED:
            return (
                <Tooltip
                    title={
                        <Localized
                            de="gesperrt"
                            fr="bloqué"
                            it="bloccato"
                            en="locked"
                        />
                    }
                    placement="right"
                >
                    <Lock sx={{ color: 'warning.main' }} />
                </Tooltip>
            );
        case CustomerTenantState.ARCHIVED:
            return (
                <Tooltip
                    title={
                        <Localized
                            de="archiviert"
                            fr="archivé"
                            it="archiviato"
                            en="archived"
                        />
                    }
                    placement="right"
                >
                    <RemoveCircleOutlineOutlined color="disabled" />
                </Tooltip>
            );
    }
}

function ParkingaboUsersSearchTooltip() {
    return (
        <Box
            sx={{
                fontSize: 14,
                padding: '18px 18px 5px 18px',
                width: '300px',
            }}
        >
            <Localized
                de={
                    <>
                        Verfügbare Suchkriterien:
                        <ul>
                            <li>Name</li>
                            <li>Referenz</li>
                            <li>Konto-ID</li>
                            <li>E-Mail-Adresse</li>
                            <li>Kennzeichen</li>
                            <li>Badgenummer</li>
                        </ul>
                    </>
                }
                fr={
                    <>
                        Critères de recherche disponibles:
                        <ul>
                            <li>nom</li>
                            <li>référence</li>
                            <li>ID compte</li>
                            <li>adresse électronique</li>
                            <li>numéro de plaque</li>
                            <li>numéro de badge</li>
                        </ul>
                    </>
                }
                it={
                    <>
                        Criteri di ricerca disponibili:
                        <ul>
                            <li>nome</li>
                            <li>referenza</li>
                            <li>ID conto</li>
                            <li>indirizzo e-mail</li>
                            <li>numero di targa</li>
                            <li>numero di badge</li>
                        </ul>
                    </>
                }
                en={
                    <>
                        Available search criteria:
                        <ul>
                            <li>name</li>
                            <li>reference</li>
                            <li>account ID</li>
                            <li>e-mail address</li>
                            <li>licence plate number</li>
                            <li>badge number</li>
                        </ul>
                    </>
                }
            />
        </Box>
    );
}
